import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import BlockIcon from "@mui/icons-material/Block";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
  DataGrid,
  GridActionsCellItem,
  GridToolbarContainer,
  zhTW,
} from "@mui/x-data-grid";
import { useTheme } from "@mui/material/styles";
import { AuthContext } from "../../context/AuthContext";
import getAllStore from "../../api/store/getAllStore";
import listInvoices from "../../api/invoice/listInvoices";
import cancelInvoice from "../../api/invoice/cancelInvoice";
import getCurrentInvoiceRange from "../../api/invoice/getCurrentInvoiceRange";
import issueTestInvoice from "../../api/invoice/issueTestInvoice";
import {
  isInvoiceDestinationValid,
  isLoveCodeValid,
  normalizeInvoiceValue,
} from "../../invoiceUtils";

const DEFAULT_PAGE_SIZE = 25;
const PENDING_REFRESH_INTERVAL_MS = 5000;

const statusLabels = {
  pending: { label: "待上傳", color: "warning" },
  uploaded: { label: "已上傳", color: "success" },
  failed: { label: "上傳失敗", color: "error" },
};

const cancellationLabels = {
  pending: { label: "作廢中", color: "warning" },
  uploaded: { label: "已作廢", color: "success" },
  failed: { label: "作廢失敗", color: "error" },
};

const payWayOptions = [
  ["1", "現金"],
  ["2", "ATM"],
  ["3", "信用卡"],
  ["4", "超商代收"],
  ["5", "其他"],
  ["6", "電子支付"],
  ["A", "Apple Pay"],
  ["E", "先享後付 Aftee"],
  ["G", "Google Pay"],
  ["J", "街口支付"],
  ["L", "Line Pay"],
  ["P", "Pi 拍錢包、慢點付"],
  ["S", "Samsung Pay"],
  ["T", "台灣 Pay"],
  ["U", "悠遊付"],
  ["W", "全盈+PAY"],
  ["X", "全支付"],
  ["Z", "銀角零卡"],
];

function currentInvoicePeriod(now = new Date()) {
  const rocYear = now.getFullYear() - 1911;
  const month = now.getMonth() + 1;
  const startMonth = month % 2 === 0 ? month - 1 : month;
  return `${rocYear}-${String(startMonth).padStart(2, "0")}-${String(
    startMonth + 1
  ).padStart(2, "0")}`;
}

function apiErrorMessage(error) {
  const data = error?.response?.data;
  return data?.message || data?.error || error?.message || "操作失敗";
}

function formatDateTime(value) {
  if (!value) {
    return "";
  }
  return new Date(value).toLocaleString("zh-TW", { timeZone: "Asia/Taipei" });
}

function formatMoney(value) {
  const amount = Number(value);
  if (!Number.isFinite(amount)) {
    return value || "";
  }
  return amount.toLocaleString("zh-TW");
}

function statusChip(status, labels) {
  const meta = labels[status] || { label: status || "-", color: "default" };
  return <Chip size="small" label={meta.label} color={meta.color} />;
}

function canCancelInvoice(row) {
  return row.status === "uploaded" && !row.cancellation;
}

function createRequestID() {
  if (typeof window.crypto?.randomUUID === "function") {
    return window.crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function createTestItem(overrides = {}) {
  return {
    key: createRequestID(),
    productionCode: "0001",
    description: "測試商品A",
    quantity: "1",
    unitPrice: "100",
    amount: "100",
    ...overrides,
  };
}

function createDefaultTestForm() {
  return {
    deliveryType: "paper",
    buyerIdentifier: "",
    carrierId1: "",
    npoban: "",
    payWay: "1",
    items: [createTestItem()],
  };
}

function itemAmount(item) {
  const supplied = Number(item.amount);
  if (item.amount !== "" && Number.isFinite(supplied)) {
    return supplied;
  }
  const quantity = Number(item.quantity);
  const unitPrice = Number(item.unitPrice);
  if (!Number.isFinite(quantity) || !Number.isFinite(unitPrice)) {
    return 0;
  }
  return Math.round(quantity * unitPrice);
}

function testInvoiceTotal(items) {
  return items.reduce((total, item) => total + itemAmount(item), 0);
}

function validateTestInvoiceForm(form) {
  if (form.deliveryType === "paper") {
    const buyerIdentifier = form.buyerIdentifier.trim();
    if (buyerIdentifier && !/^\d{8}$/.test(buyerIdentifier)) {
      return "統一編號必須是 8 位數字";
    }
  } else if (form.deliveryType === "carrier") {
    if (!isInvoiceDestinationValid("carrier", form.carrierId1)) {
      return "請輸入正確的手機載具條碼";
    }
  } else if (form.deliveryType === "donate") {
    if (!isLoveCodeValid(form.npoban)) {
      return "請輸入 3 至 7 位數字的愛心碼";
    }
  } else {
    return "請選擇開立方式";
  }

  if (form.items.length === 0) {
    return "至少需要一筆商品明細";
  }
  for (let index = 0; index < form.items.length; index += 1) {
    const item = form.items[index];
    if (!item.description.trim()) {
      return `第 ${index + 1} 筆商品缺少商品描述`;
    }
    if (!Number.isFinite(Number(item.quantity)) || Number(item.quantity) <= 0) {
      return `第 ${index + 1} 筆商品數量必須大於 0`;
    }
    if (!Number.isFinite(Number(item.unitPrice)) || Number(item.unitPrice) < 0) {
      return `第 ${index + 1} 筆商品單價不可小於 0`;
    }
    if (item.amount !== "" && (!Number.isFinite(Number(item.amount)) || Number(item.amount) < 0)) {
      return `第 ${index + 1} 筆商品金額不可小於 0`;
    }
  }
  if (testInvoiceTotal(form.items) <= 0) {
    return "發票總金額必須大於 0";
  }
  return "";
}

function InvoiceToolbar({ onRefresh }) {
  return (
    <GridToolbarContainer>
      <Button color="info" startIcon={<RefreshIcon />} onClick={onRefresh}>
        刷新
      </Button>
    </GridToolbarContainer>
  );
}

function RangeMetricCard({ label, value, helper, loading }) {
  return (
    <Card variant="outlined" sx={{ height: "100%" }}>
      <CardContent>
        <Typography color="text.secondary" variant="body2" gutterBottom>
          {label}
        </Typography>
        <Stack direction="row" alignItems="center" minHeight={38}>
          {loading ? (
            <CircularProgress size={24} aria-label={`${label}讀取中`} />
          ) : (
            <Typography variant="h6" sx={{ wordBreak: "break-word" }}>
              {value}
            </Typography>
          )}
        </Stack>
        <Typography color="text.secondary" variant="caption">
          {helper}
        </Typography>
      </CardContent>
    </Card>
  );
}

function TestInvoiceDialog({
  open,
  onClose,
  onIssue,
  store,
  endpointMode,
  submitting,
}) {
  const storeCode = store?.Code || "";
  const [form, setForm] = useState(createDefaultTestForm);
  const [confirming, setConfirming] = useState(false);
  const [requestID, setRequestID] = useState("");
  const [localError, setLocalError] = useState("");

  useEffect(() => {
    if (!open) {
      return;
    }
    setForm(createDefaultTestForm());
    setConfirming(false);
    setRequestID("");
    setLocalError("");
  }, [open, storeCode]);

  const updateForm = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
    setRequestID("");
    setLocalError("");
  };

  const updateDeliveryType = (deliveryType) => {
    setForm((current) => ({
      ...current,
      deliveryType,
      buyerIdentifier: "",
      carrierId1: "",
      npoban: "",
    }));
    setRequestID("");
    setLocalError("");
  };

  const updateItem = (itemIndex, field, value) => {
    setForm((current) => ({
      ...current,
      items: current.items.map((item, index) =>
        index === itemIndex ? { ...item, [field]: value } : item
      ),
    }));
    setRequestID("");
    setLocalError("");
  };

  const removeItem = (itemIndex) => {
    setForm((current) => ({
      ...current,
      items: current.items.filter((_, index) => index !== itemIndex),
    }));
    setRequestID("");
    setLocalError("");
  };

  const handleReview = () => {
    const validationError = validateTestInvoiceForm(form);
    if (validationError) {
      setLocalError(validationError);
      return;
    }
    if (!requestID) {
      setRequestID(createRequestID());
    }
    setLocalError("");
    setConfirming(true);
  };

  const handleConfirm = async () => {
    const normalizedCarrier = normalizeInvoiceValue("carrier", form.carrierId1);
    const payload = {
      requestId: requestID,
      deliveryType: form.deliveryType,
      buyerIdentifier:
        form.deliveryType === "paper" ? form.buyerIdentifier.trim() : "",
      carrierId1: form.deliveryType === "carrier" ? normalizedCarrier : "",
      npoban: form.deliveryType === "donate" ? form.npoban.trim() : "",
      payWay: form.payWay,
      items: form.items.map((item) => ({
        productionCode: item.productionCode.trim(),
        description: item.description.trim(),
        quantity: item.quantity.trim(),
        unitPrice: item.unitPrice.trim(),
        amount: item.amount.trim(),
      })),
    };
    try {
      await onIssue(payload);
      onClose();
    } catch (error) {
      setLocalError(apiErrorMessage(error));
    }
  };

  const modeLabel = endpointMode === "production" ? "正式端點" : "測試端點";
  const total = testInvoiceTotal(form.items);

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onClose}
      maxWidth="md"
      fullWidth
      aria-labelledby="test-invoice-dialog-title"
    >
      <DialogTitle id="test-invoice-dialog-title">
        {confirming ? "確認測試開立" : "測試開立發票"}
      </DialogTitle>
      <DialogContent dividers>
        {localError ? (
          <Alert severity="error" sx={{ marginBottom: 2 }}>
            {localError}
          </Alert>
        ) : null}

        {confirming ? (
          <Stack spacing={2}>
            <Alert severity={endpointMode === "production" ? "error" : "warning"}>
              {endpointMode === "production"
                ? "這會開出真實電子發票並消耗正式字軌，請確認店家與金額無誤。"
                : "這會消耗一組測試字軌號碼，並將發票送往 Cetustek 測試端點。"}
            </Alert>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <Typography variant="h6">{store?.Name}</Typography>
              <Chip label={modeLabel} color={endpointMode === "production" ? "error" : "info"} />
            </Stack>
            <Typography>Console 店號：{store?.Code}</Typography>
            <Typography>發票系統店號：{store?.ImaoStoreCode}</Typography>
            <Typography>商品筆數：{form.items.length}</Typography>
            <Typography variant="h6">總金額：NT$ {formatMoney(total)}</Typography>
          </Stack>
        ) : (
          <Stack spacing={3}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel id="test-invoice-delivery-label">開立方式</InputLabel>
                  <Select
                    labelId="test-invoice-delivery-label"
                    label="開立方式"
                    value={form.deliveryType}
                    onChange={(event) => updateDeliveryType(event.target.value)}
                  >
                    <MenuItem value="paper">紙本</MenuItem>
                    <MenuItem value="carrier">手機載具</MenuItem>
                    <MenuItem value="donate">捐贈</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel id="test-invoice-payway-label">付款方式</InputLabel>
                  <Select
                    labelId="test-invoice-payway-label"
                    label="付款方式"
                    value={form.payWay}
                    onChange={(event) => updateForm("payWay", event.target.value)}
                  >
                    {payWayOptions.map(([code, label]) => (
                      <MenuItem key={code} value={code}>
                        {code} {label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              {form.deliveryType === "paper" ? (
                <Grid item xs={12}>
                  <TextField
                    label="統一編號（選填）"
                    value={form.buyerIdentifier}
                    onChange={(event) => updateForm("buyerIdentifier", event.target.value)}
                    inputProps={{ inputMode: "numeric", maxLength: 8 }}
                    fullWidth
                  />
                </Grid>
              ) : null}
              {form.deliveryType === "carrier" ? (
                <Grid item xs={12}>
                  <TextField
                    label="手機載具"
                    value={form.carrierId1}
                    onChange={(event) => updateForm("carrierId1", event.target.value)}
                    placeholder="/ABC+123"
                    fullWidth
                    required
                  />
                </Grid>
              ) : null}
              {form.deliveryType === "donate" ? (
                <Grid item xs={12}>
                  <TextField
                    label="愛心碼"
                    value={form.npoban}
                    onChange={(event) => updateForm("npoban", event.target.value)}
                    inputProps={{ inputMode: "numeric", maxLength: 7 }}
                    fullWidth
                    required
                  />
                </Grid>
              ) : null}
            </Grid>

            <Divider />

            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="h6">商品明細</Typography>
              <Button
                startIcon={<AddIcon />}
                onClick={() => {
                  setForm((current) => ({
                    ...current,
                    items: [
                      ...current.items,
                      createTestItem({
                        productionCode: "",
                        description: "",
                        quantity: "1",
                        unitPrice: "",
                        amount: "",
                      }),
                    ],
                  }));
                  setRequestID("");
                }}
              >
                新增商品
              </Button>
            </Stack>

            {form.items.map((item, index) => (
              <Card key={item.key} variant="outlined">
                <CardContent>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" marginBottom={2}>
                    <Typography fontWeight={600}>商品 {index + 1}</Typography>
                    <Tooltip title="刪除商品">
                      <span>
                        <IconButton
                          aria-label={`刪除商品 ${index + 1}`}
                          onClick={() => removeItem(index)}
                          disabled={form.items.length === 1}
                          color="error"
                        >
                          <DeleteOutlineIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Stack>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={4} md={2}>
                      <TextField
                        label="品名代號"
                        value={item.productionCode}
                        onChange={(event) => updateItem(index, "productionCode", event.target.value)}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={8} md={4}>
                      <TextField
                        label="商品描述"
                        value={item.description}
                        onChange={(event) => updateItem(index, "description", event.target.value)}
                        fullWidth
                        required
                      />
                    </Grid>
                    <Grid item xs={6} sm={4} md={2}>
                      <TextField
                        label="數量"
                        value={item.quantity}
                        onChange={(event) => updateItem(index, "quantity", event.target.value)}
                        inputProps={{ inputMode: "decimal" }}
                        fullWidth
                        required
                      />
                    </Grid>
                    <Grid item xs={6} sm={4} md={2}>
                      <TextField
                        label="單價"
                        value={item.unitPrice}
                        onChange={(event) => updateItem(index, "unitPrice", event.target.value)}
                        inputProps={{ inputMode: "decimal" }}
                        fullWidth
                        required
                      />
                    </Grid>
                    <Grid item xs={12} sm={4} md={2}>
                      <TextField
                        label="金額"
                        value={item.amount}
                        onChange={(event) => updateItem(index, "amount", event.target.value)}
                        helperText="留空時由數量與單價計算"
                        inputProps={{ inputMode: "decimal" }}
                        fullWidth
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            ))}
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ padding: 2 }}>
        {confirming ? (
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => setConfirming(false)}
            disabled={submitting}
          >
            返回修改
          </Button>
        ) : (
          <Button onClick={onClose} disabled={submitting}>
            取消
          </Button>
        )}
        <Box flexGrow={1} />
        {confirming ? (
          <Button
            variant="contained"
            color={endpointMode === "production" ? "error" : "secondary"}
            onClick={handleConfirm}
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : <ReceiptLongIcon />}
          >
            {submitting ? "開立中" : "確認開立"}
          </Button>
        ) : (
          <Button variant="contained" color="secondary" onClick={handleReview}>
            下一步
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

function InvoicePage() {
  const theme = useTheme();
  const userContext = useContext(AuthContext);
  const [stores, setStores] = useState([]);
  const [storeCode, setStoreCode] = useState("");
  const [period, setPeriod] = useState(() => currentInvoicePeriod());
  const [rows, setRows] = useState([]);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  });
  const [rowCount, setRowCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [rangeStatus, setRangeStatus] = useState(null);
  const [rangeLoading, setRangeLoading] = useState(false);
  const [rangeError, setRangeError] = useState("");
  const [testDialogOpen, setTestDialogOpen] = useState(false);
  const [testLoading, setTestLoading] = useState(false);
  const [dialogInvoice, setDialogInvoice] = useState(null);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelLoading, setCancelLoading] = useState(false);

  const visibleStores = useMemo(() => {
    const invoiceStores = (stores || []).filter(
      (store) => store.InvoiceEnable && store.ImaoStoreCode
    );
    if (userContext.user?.Role !== "Moderator") {
      return invoiceStores;
    }
    const allowedStores = new Set(
      (userContext.user?.Permissions || []).map((permission) => permission.store)
    );
    return invoiceStores.filter((store) => allowedStores.has(store.Code));
  }, [stores, userContext.user]);

  const selectedStore = useMemo(
    () => visibleStores.find((store) => store.Code === storeCode) || null,
    [storeCode, visibleStores]
  );

  const fetchStores = useCallback(async () => {
    try {
      const data = await getAllStore();
      setStores(data?.stores || []);
      setError("");
    } catch (err) {
      setError(apiErrorMessage(err));
    }
  }, []);

  const fetchInvoices = useCallback(async () => {
    if (!storeCode || !period) {
      setRows([]);
      setRowCount(0);
      return;
    }
    try {
      setLoading(true);
      const data = await listInvoices({
        storeCode,
        period,
        page: paginationModel.page + 1,
        pageSize: paginationModel.pageSize,
      });
      setRows(data?.invoices || []);
      setRowCount(data?.pagination?.total || 0);
      setError("");
    } catch (err) {
      setRows([]);
      setRowCount(0);
      setError(apiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [paginationModel.page, paginationModel.pageSize, period, storeCode]);

  const fetchRange = useCallback(async () => {
    if (!storeCode) {
      setRangeStatus(null);
      setRangeError("");
      return;
    }
    try {
      setRangeLoading(true);
      const data = await getCurrentInvoiceRange({ storeCode });
      setRangeStatus(data);
      setRangeError("");
    } catch (err) {
      setRangeStatus(null);
      setRangeError(apiErrorMessage(err));
    } finally {
      setRangeLoading(false);
    }
  }, [storeCode]);

  useEffect(() => {
    fetchStores();
  }, [fetchStores]);

  useEffect(() => {
    if (visibleStores.length === 0) {
      if (storeCode) {
        setStoreCode("");
      }
      return;
    }
    if (!visibleStores.some((store) => store.Code === storeCode)) {
      setStoreCode(visibleStores[0].Code);
    }
  }, [storeCode, visibleStores]);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  useEffect(() => {
    fetchRange();
  }, [fetchRange]);

  const hasPending = useMemo(
    () =>
      rows.some(
        (row) => row.status === "pending" || row.cancellation?.status === "pending"
      ),
    [rows]
  );

  useEffect(() => {
    if (!hasPending) {
      return undefined;
    }
    const timer = window.setInterval(fetchInvoices, PENDING_REFRESH_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [fetchInvoices, hasPending]);

  const handleCancelSubmit = async () => {
    if (!dialogInvoice) {
      return;
    }
    try {
      setCancelLoading(true);
      await cancelInvoice({
        storeCode,
        invoiceNumber: dialogInvoice.invoiceNumber,
        reason: cancelReason,
      });
      setDialogInvoice(null);
      setCancelReason("");
      await fetchInvoices();
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setCancelLoading(false);
    }
  };

  const handleIssueTestInvoice = async (payload) => {
    try {
      setTestLoading(true);
      setError("");
      setSuccess("");
      const result = await issueTestInvoice({ storeCode, ...payload });
      setSuccess(`發票 ${result.invoiceNumber} 已建立，正在等待平台上傳。`);
      await fetchRange();
      if (result.period !== period || paginationModel.page !== 0) {
        setPeriod(result.period);
        setPaginationModel((current) => ({ ...current, page: 0 }));
      } else {
        await fetchInvoices();
      }
      return result;
    } catch (err) {
      setError(apiErrorMessage(err));
      throw err;
    } finally {
      setTestLoading(false);
    }
  };

  const columns = useMemo(
    () => [
      { field: "orderNo", headerName: "訂單編號", width: 180 },
      { field: "invoiceNumber", headerName: "發票號碼", width: 130 },
      {
        field: "issuedAt",
        headerName: "開立時間",
        width: 190,
        valueGetter: ({ row }) => formatDateTime(row.issuedAt),
      },
      {
        field: "totalAmount",
        headerName: "金額",
        width: 100,
        align: "right",
        headerAlign: "right",
        valueGetter: ({ row }) => formatMoney(row.totalAmount),
      },
      {
        field: "status",
        headerName: "上傳狀態",
        width: 130,
        renderCell: ({ row }) => (
          <Tooltip title={row.failReason || ""}>
            <span>{statusChip(row.status, statusLabels)}</span>
          </Tooltip>
        ),
      },
      {
        field: "cancellation",
        headerName: "作廢狀態",
        width: 140,
        renderCell: ({ row }) => {
          if (!row.cancellation) {
            return <Chip size="small" label="未作廢" variant="outlined" />;
          }
          return (
            <Tooltip title={row.cancellation.failReason || ""}>
              <span>{statusChip(row.cancellation.status, cancellationLabels)}</span>
            </Tooltip>
          );
        },
      },
      {
        field: "actions",
        type: "actions",
        headerName: "操作",
        width: 80,
        getActions: ({ row }) => [
          <GridActionsCellItem
            icon={<BlockIcon />}
            label="作廢"
            disabled={!canCancelInvoice(row)}
            onClick={() => {
              setDialogInvoice(row);
              setCancelReason("");
            }}
            color="inherit"
            showInMenu={false}
          />,
        ],
      },
    ],
    []
  );

  const currentRange = rangeStatus?.range;
  const rangeAvailable = rangeStatus?.status === "available";
  const nextInvoiceValue =
    rangeStatus?.status === "missing"
      ? "尚未取得"
      : rangeStatus?.status === "exhausted"
      ? "本期已用完"
      : currentRange?.nextInvoiceNumber || "—";
  const rangeValue = currentRange
    ? `${currentRange.track}${currentRange.startNo} ～ ${currentRange.track}${currentRange.endNo}`
    : "—";
  const remainingValue = currentRange ? formatMoney(currentRange.remaining) : "—";

  return (
    <Box>
      <Stack
        direction={{ xs: "column", md: "row" }}
        alignItems={{ xs: "stretch", md: "center" }}
        spacing={2}
        marginBottom={2}
      >
        <FormControl sx={{ minWidth: 220, bgcolor: "background.paper" }} size="small">
          <InputLabel id="invoice-store-label">店家</InputLabel>
          <Select
            labelId="invoice-store-label"
            label="店家"
            value={storeCode}
            onChange={(event) => {
              setStoreCode(event.target.value);
              setRangeStatus(null);
              setRangeError("");
              setSuccess("");
              setPaginationModel((current) => ({ ...current, page: 0 }));
            }}
          >
            {visibleStores.map((store) => (
              <MenuItem key={store.Code} value={store.Code}>
                {store.Name} ({store.Code})
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          label="期別"
          size="small"
          color="secondary"
          value={period}
          onChange={(event) => {
            setPeriod(event.target.value);
            setPaginationModel((current) => ({ ...current, page: 0 }));
          }}
          sx={{ width: { xs: "100%", md: 150 }, bgcolor: "background.paper" }}
        />
        <Button
          variant="contained"
          color="secondary"
          startIcon={<RefreshIcon />}
          onClick={() => Promise.all([fetchInvoices(), fetchRange()])}
          disabled={!storeCode || !period}
          sx={{ minHeight: 40 }}
        >
          查詢
        </Button>
        <Tooltip
          title={rangeAvailable ? "" : "目前沒有可使用的當期字軌"}
          disableHoverListener={rangeAvailable}
        >
          <span>
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<ReceiptLongIcon />}
              onClick={() => setTestDialogOpen(true)}
              disabled={!selectedStore || !rangeAvailable}
              sx={{ minHeight: 40, width: { xs: "100%", md: "auto" } }}
            >
              測試開立
            </Button>
          </span>
        </Tooltip>
      </Stack>

      {error ? (
        <Alert severity="error" sx={{ marginBottom: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      ) : null}
      {rangeError ? (
        <Alert severity="error" sx={{ marginBottom: 2 }}>
          字軌狀態讀取失敗：{rangeError}
        </Alert>
      ) : null}
      {success ? (
        <Alert severity="success" sx={{ marginBottom: 2 }} onClose={() => setSuccess("")}>
          {success}
        </Alert>
      ) : null}

      {visibleStores.length === 0 ? (
        <Typography>沒有可管理的發票店家</Typography>
      ) : (
        <Stack spacing={2}>
          <Card variant="outlined">
            <CardContent>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                justifyContent="space-between"
                alignItems={{ xs: "flex-start", sm: "center" }}
                spacing={1}
                marginBottom={2}
              >
                <Box>
                  <Typography variant="h6">當期發票區間狀態</Typography>
                  <Typography color="text.secondary" variant="body2">
                    期別 {rangeStatus?.period || currentInvoicePeriod()} ・ 發票系統店號 {selectedStore?.ImaoStoreCode}
                  </Typography>
                </Box>
                {rangeStatus?.endpointMode ? (
                  <Chip
                    label={rangeStatus.endpointMode === "production" ? "正式端點" : "測試端點"}
                    color={rangeStatus.endpointMode === "production" ? "error" : "info"}
                    variant={rangeStatus.endpointMode === "production" ? "filled" : "outlined"}
                  />
                ) : null}
              </Stack>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <RangeMetricCard
                    label="下一張發票"
                    value={nextInvoiceValue}
                    helper={rangeStatus?.status === "missing" ? "服務會持續自動取號" : "開立時才會消耗號碼"}
                    loading={rangeLoading && !rangeStatus}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <RangeMetricCard
                    label="當期區間"
                    value={rangeValue}
                    helper="目前正在使用的有效區間"
                    loading={rangeLoading && !rangeStatus}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <RangeMetricCard
                    label="剩餘數量"
                    value={remainingValue}
                    helper="不包含尚未切換的備援區間"
                    loading={rangeLoading && !rangeStatus}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <Box sx={{ height: 650, width: "100%", bgcolor: "background.paper" }}>
            <DataGrid
              localeText={zhTW.components.MuiDataGrid.defaultProps.localeText}
              rows={rows}
              columns={columns}
              getRowId={(row) => row.invoiceNumber}
              loading={loading}
              disableColumnSelector
              disableRowSelectionOnClick
              paginationMode="server"
              rowCount={rowCount}
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              pageSizeOptions={[10, 25, 50, 100]}
              sx={{ color: theme.palette.text.primary }}
              slots={{
                toolbar: InvoiceToolbar,
              }}
              slotProps={{
                toolbar: { onRefresh: fetchInvoices },
              }}
            />
          </Box>
        </Stack>
      )}

      <TestInvoiceDialog
        open={testDialogOpen}
        onClose={() => setTestDialogOpen(false)}
        onIssue={handleIssueTestInvoice}
        store={selectedStore}
        endpointMode={rangeStatus?.endpointMode}
        submitting={testLoading}
      />

      <Dialog open={Boolean(dialogInvoice)} onClose={() => setDialogInvoice(null)}>
        <DialogTitle>確認作廢發票</DialogTitle>
        <DialogContent>
          <Stack spacing={1} marginTop={1} minWidth={{ xs: 0, sm: 360 }}>
            <Typography>發票號碼：{dialogInvoice?.invoiceNumber}</Typography>
            <Typography>開立時間：{formatDateTime(dialogInvoice?.issuedAt)}</Typography>
            <Typography>金額：{formatMoney(dialogInvoice?.totalAmount)}</Typography>
            <Alert severity="warning">作廢送出後會排入上游處理，不能復原。</Alert>
            <TextField
              label="作廢原因"
              value={cancelReason}
              onChange={(event) => setCancelReason(event.target.value)}
              fullWidth
              color="secondary"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogInvoice(null)} disabled={cancelLoading}>
            取消
          </Button>
          <Button
            onClick={handleCancelSubmit}
            variant="contained"
            color="error"
            disabled={cancelLoading}
          >
            確認作廢
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default InvoicePage;
